import { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import { Star, Plus, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePerformanceStore, PerformanceReview } from '../../../store/performanceStore';
import { useEmployeeStore } from '../../../store/employeeStore';

const STATUSES = ['draft', 'submitted', 'completed'];
const RATINGS = [1, 2, 3, 4, 5];

const EMPTY_FORM = {
    employeeId: '', reviewerId: '', periodStart: '', periodEnd: '',
    rating: '', status: 'draft', strengths: '', weaknesses: '', goals: '', comments: '',
};

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

function Stars({ rating }: { rating: number | null }) {
    if (!rating) return <span className="text-muted-foreground">—</span>;
    return (
        <span className="inline-flex items-center gap-0.5">
            {RATINGS.map((n) => (
                <Star
                    key={n}
                    className={`w-3.5 h-3.5 ${n <= rating ? 'fill-warning text-warning' : 'text-border'}`}
                />
            ))}
        </span>
    );
}

export function PerformanceHub() {
    const { reviews, stats, isLoading, fetchReviews, fetchStats, createReview, updateReview, deleteReview } = usePerformanceStore();
    const { employees, fetchEmployees } = useEmployeeStore();

    const [isFormOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchReviews();
        fetchStats();
        fetchEmployees({ limit: 200 });
    }, []);

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setErrors({});
        setFormOpen(true);
    };

    const openEdit = (r: PerformanceReview) => {
        setEditingId(r.id);
        setForm({
            employeeId: r.employeeId ?? '',
            reviewerId: r.reviewerId ?? '',
            periodStart: r.periodStart ? String(r.periodStart).slice(0, 10) : '',
            periodEnd: r.periodEnd ? String(r.periodEnd).slice(0, 10) : '',
            rating: r.rating ? String(r.rating) : '',
            status: r.status ?? 'draft',
            strengths: r.strengths ?? '',
            weaknesses: r.weaknesses ?? '',
            goals: r.goals ?? '',
            comments: r.comments ?? '',
        });
        setErrors({});
        setFormOpen(true);
    };

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.employeeId) next.employeeId = 'Select an employee';
        if (form.periodStart && form.periodEnd && form.periodStart > form.periodEnd) {
            next.periodEnd = 'End date must be after start date';
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                employeeId: form.employeeId,
                reviewerId: form.reviewerId || null,
                periodStart: form.periodStart || null,
                periodEnd: form.periodEnd || null,
                rating: form.rating ? Number(form.rating) : null,
                status: form.status,
                strengths: form.strengths.trim(),
                weaknesses: form.weaknesses.trim(),
                goals: form.goals.trim(),
                comments: form.comments.trim(),
            };
            if (editingId) {
                await updateReview(editingId, payload);
                toast.success('Review updated');
            } else {
                await createReview(payload);
                toast.success('Review created');
            }
            setFormOpen(false);
        } catch (e) {
            toast.error(editingId ? 'Failed to update review' : 'Failed to create review');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteReview(deleteTarget.id);
            toast.success('Review deleted');
            setDeleteTarget(null);
        } catch (e) {
            toast.error('Failed to delete review');
        } finally {
            setDeleting(false);
        }
    };

    const statusVariant = (s: string) =>
        s === 'completed' ? 'success' : s === 'submitted' ? 'info' : 'secondary';

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Performance Reviews</h1>
                    <p className="text-muted-foreground mt-1">Employee evaluations, ratings, and goals</p>
                </div>
                <Button variant="primary" onClick={openCreate}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Review
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card><CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Total Reviews</p>
                    <h3 className="text-foreground font-display text-metric">{stats?.total ?? reviews.length}</h3>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Drafts</p>
                    <h3 className="text-foreground font-display text-metric">{stats?.draft ?? '—'}</h3>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <h3 className="text-foreground font-display text-metric">{stats?.completed ?? '—'}</h3>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Average Rating</p>
                    <h3 className="text-foreground font-display text-metric">
                        {stats?.average_rating != null ? Number(stats.average_rating).toFixed(2) : '—'}
                    </h3>
                </CardContent></Card>
            </div>

            {/* Reviews table */}
            <Card>
                <CardHeader>
                    <h3 className="text-foreground font-display text-section-title">All Reviews</h3>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">Employee</th>
                                    <th className="px-6 py-3">Reviewer</th>
                                    <th className="px-6 py-3">Period</th>
                                    <th className="px-6 py-3">Rating</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {reviews.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                                            No performance reviews yet. Create the first one.
                                        </td>
                                    </tr>
                                ) : reviews.map((r) => (
                                    <tr key={r.id} className="hover:bg-secondary/20 transition-colors align-top">
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-foreground">{r.employeeName}</div>
                                            {r.employeeCode && <div className="text-xs text-muted-foreground">{r.employeeCode}</div>}
                                        </td>
                                        <td className="px-6 py-4">{r.reviewerName || <span className="text-muted-foreground">Unassigned</span>}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{fmtDate(r.periodStart)} – {fmtDate(r.periodEnd)}</td>
                                        <td className="px-6 py-4"><Stars rating={r.rating} /></td>
                                        <td className="px-6 py-4">
                                            <Badge variant={statusVariant(r.status)}>{r.status.toUpperCase()}</Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button variant="ghost" size="icon-sm" onClick={() => openEdit(r)}
                                                    className="text-muted-foreground hover:text-foreground" aria-label="Edit review">
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon-sm" onClick={() => setDeleteTarget({ id: r.id, name: r.employeeName })}
                                                    className="text-muted-foreground hover:text-destructive" aria-label="Delete review">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Create / Edit modal */}
            <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Edit Review' : 'New Performance Review'}</DialogTitle>
                        <DialogDescription>
                            {editingId ? 'Update the review details below.' : 'Evaluate an employee for a review period.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Employee</label>
                                <Select value={form.employeeId} onValueChange={(v) => setForm((f) => ({ ...f, employeeId: v }))}>
                                    <SelectTrigger invalid={!!errors.employeeId}>
                                        <SelectValue placeholder="Select employee" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {employees.map((e) => (
                                            <SelectItem key={e.id} value={e.id}>
                                                {e.displayName || `${e.firstName} ${e.lastName}`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.employeeId && <p className="text-xs text-destructive">{errors.employeeId}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Reviewer (optional)</label>
                                <Select value={form.reviewerId} onValueChange={(v) => setForm((f) => ({ ...f, reviewerId: v }))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select reviewer" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {employees.map((e) => (
                                            <SelectItem key={e.id} value={e.id}>
                                                {e.displayName || `${e.firstName} ${e.lastName}`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Period Start</label>
                                <Input type="date" value={form.periodStart}
                                    onChange={(e) => setForm((f) => ({ ...f, periodStart: e.target.value }))} />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Period End</label>
                                <Input type="date" value={form.periodEnd}
                                    onChange={(e) => setForm((f) => ({ ...f, periodEnd: e.target.value }))}
                                    invalid={!!errors.periodEnd} />
                                {errors.periodEnd && <p className="text-xs text-destructive">{errors.periodEnd}</p>}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Rating</label>
                                <Select value={form.rating} onValueChange={(v) => setForm((f) => ({ ...f, rating: v }))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Not rated" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {RATINGS.map((n) => (
                                            <SelectItem key={n} value={String(n)}>{n} / 5</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Status</label>
                                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUSES.map((s) => (
                                            <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Strengths</label>
                            <Textarea rows={2} value={form.strengths}
                                onChange={(e) => setForm((f) => ({ ...f, strengths: e.target.value }))}
                                placeholder="Key strengths demonstrated…" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Areas for Improvement</label>
                            <Textarea rows={2} value={form.weaknesses}
                                onChange={(e) => setForm((f) => ({ ...f, weaknesses: e.target.value }))}
                                placeholder="Areas to develop…" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Goals <span className="text-muted-foreground font-normal">(one per line)</span></label>
                            <Textarea rows={3} value={form.goals}
                                onChange={(e) => setForm((f) => ({ ...f, goals: e.target.value }))}
                                placeholder={'Ship the reporting module\nImprove code review turnaround'} />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Comments</label>
                            <Textarea rows={2} value={form.comments}
                                onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
                                placeholder="Overall summary…" />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
                        <Button variant="primary" onClick={handleSubmit} isLoading={saving}>
                            {editingId ? 'Save Changes' : 'Create Review'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Delete review</DialogTitle>
                        <DialogDescription>
                            Delete the review for <span className="font-medium text-foreground">{deleteTarget?.name}</span>? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
